import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import Loading from '../../../../components/loading';
import NoData from '../../../../components/no-data';
import { GT_COLORS } from '../../../../constants/designTokens.constants';
import { TEST_MODE_COPY } from '../testMode.copy';
import { TestModeStatusRow, deriveRowStatus } from '../testMode.types';

dayjs.extend(relativeTime);

const COPY = TEST_MODE_COPY.table;

const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return `${first}${last}`.toUpperCase() || '?';
};

/** Pill chip with a status dot, matching the prototype's row chips. */
const StatusChip: React.FC<{ label: string; accent: string; text: string }> = ({ label, accent, text }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            px: '10px',
            py: '4px',
            borderRadius: '999px',
            backgroundColor: alpha(accent, 0.15),
            color: text,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
        }}
    >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: accent }} />
        {label}
    </Box>
);

/** Small rounded badge for the App test / Device cells. */
const CellBadge: React.FC<{
    label: string;
    tone: 'success' | 'warning' | 'muted';
    icon?: React.ReactNode;
    title?: string;
}> = ({ label, tone, icon, title }) => {
    const theme = useTheme();
    const tones = {
        success: { bg: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.dark },
        warning: { bg: alpha(theme.palette.warning.main, 0.16), color: (theme.palette.warning as any).darker ?? theme.palette.warning.dark },
        muted: { bg: theme.palette.grey[200], color: theme.palette.text.disabled },
    } as const;
    const t = tones[tone];
    const badge = (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                px: '9px',
                py: '3px',
                borderRadius: '8px',
                backgroundColor: t.bg,
                color: t.color,
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
            }}
        >
            {icon}
            {label}
        </Box>
    );
    return title ? <Tooltip title={title} arrow>{badge}</Tooltip> : badge;
};

const EmptyCell: React.FC = () => (
    <Typography component="span" sx={{ color: 'grey.300' }}>—</Typography>
);

const statusChipFor = (row: TestModeStatusRow, theme: Theme) => {
    const status = deriveRowStatus(row);
    if (status === 'flagged') {
        return <StatusChip label={COPY.status.flagged} accent={theme.palette.error.main} text={theme.palette.error.dark} />;
    }
    if (status === 'test') {
        return (
            <StatusChip
                label={COPY.status.inTestMode}
                accent={theme.palette.warning.main}
                text={(theme.palette.warning as any).darker ?? theme.palette.warning.dark}
            />
        );
    }
    return (
        <StatusChip
            label={COPY.status.live}
            accent={theme.palette.success.main}
            text={(theme.palette.success as any).darker ?? theme.palette.success.dark}
        />
    );
};

interface Props {
    rows: TestModeStatusRow[];
    total: number;
    page: number;
    perPage: number;
    loading: boolean;
    /** Fetch failed — render an explicit error state, never the empty state. */
    loadError: boolean;
    hasActiveFilters: boolean;
    allClear: boolean;
    selected: Record<string, boolean>;
    /** Bulk "put into test mode" is admin-only (route permission mirror). */
    canBulkEnroll: boolean;
    onToggleRow: (userId: string) => void;
    onToggleAllOnPage: (userIds: string[], select: boolean) => void;
    onClearSelection: () => void;
    onPageChange: (page: number) => void;
    onPerPageChange: (perPage: number) => void;
    onClearFilters: () => void;
    onBulkRequestExit: () => void;
    onBulkEnroll: () => void;
    onRequestExit: (row: TestModeStatusRow) => void;
}

/**
 * Zone 2 of the Test Mode tab: the test-status roster with row multi-select,
 * the bulk-action bar, and server-side pagination. Bulk buttons render their
 * enabled/disabled design states here; the mutations land in the actions
 * slice (they stay disabled until then).
 */
export const StatusTable: React.FC<Props> = ({
    rows,
    total,
    page,
    perPage,
    loading,
    loadError,
    hasActiveFilters,
    allClear,
    selected,
    canBulkEnroll,
    onToggleRow,
    onToggleAllOnPage,
    onClearSelection,
    onPageChange,
    onPerPageChange,
    onClearFilters,
    onBulkRequestExit,
    onBulkEnroll,
    onRequestExit,
}) => {
    const theme = useTheme();
    const border = `1px solid ${alpha(theme.palette.grey[500], 0.16)}`;

    const pageIds = rows.map((r) => r.userId);
    const selectedOnPage = pageIds.filter((id) => selected[id]);
    const selCount = Object.values(selected).filter(Boolean).length;
    const allSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;
    const someSelected = selectedOnPage.length > 0 && !allSelected;

    const headerSx = { fontSize: 12.5, fontWeight: 600, color: 'grey.700', whiteSpace: 'nowrap' } as const;

    return (
        <Card sx={{ borderRadius: '16px', border, overflow: 'hidden' }}>
            {/* selection / bulk bar */}
            <Stack
                direction="row"
                alignItems="center"
                gap="14px"
                sx={{ px: '20px', py: '11px', backgroundColor: GT_COLORS.headerBg, borderBottom: border }}
            >
                <Button
                    size="small"
                    variant="contained"
                    disabled={selCount === 0}
                    onClick={onBulkRequestExit}
                    startIcon={<CheckIcon sx={{ fontSize: 15 }} />}
                    sx={{ borderRadius: '8px', fontWeight: 600, fontSize: 13, boxShadow: 'none' }}
                >
                    {selCount > 0
                        ? TEST_MODE_COPY.selection.bulkRequestExit(selCount)
                        : TEST_MODE_COPY.selection.bulkRequestExitEmpty}
                </Button>
                {canBulkEnroll && (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selCount === 0}
                        onClick={onBulkEnroll}
                        sx={{ borderRadius: '8px', fontWeight: 600, fontSize: 13 }}
                    >
                        {selCount > 0
                            ? TEST_MODE_COPY.selection.bulkEnroll(selCount)
                            : TEST_MODE_COPY.selection.bulkEnrollEmpty}
                    </Button>
                )}
                {selCount > 0 ? (
                    <>
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {TEST_MODE_COPY.selection.selectedCount(selCount)}
                        </Typography>
                        <Button size="small" onClick={onClearSelection} sx={{ fontSize: 12.5, fontWeight: 600 }}>
                            {TEST_MODE_COPY.selection.clearSelection}
                        </Button>
                    </>
                ) : (
                    <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                        {TEST_MODE_COPY.selection.hint}
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {TEST_MODE_COPY.selection.userCount(rows.length, total)}
                </Typography>
            </Stack>

            {/* all-clear banner */}
            {allClear && !loading && (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap="14px"
                    sx={{
                        m: '16px 20px 4px',
                        p: '16px 18px',
                        borderRadius: '12px',
                        backgroundColor: alpha(theme.palette.success.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '11px',
                            backgroundColor: 'success.main',
                            color: 'common.white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <CheckIcon />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: (theme.palette.success as any).darker ?? theme.palette.success.dark }}>
                            {TEST_MODE_COPY.allClear.title}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'success.dark', mt: '2px' }}>
                            {TEST_MODE_COPY.allClear.body}
                        </Typography>
                    </Box>
                </Stack>
            )}

            {loading ? (
                <Loading />
            ) : loadError ? (
                <Alert severity="error" sx={{ m: '16px 20px' }}>
                    {TEST_MODE_COPY.errors.roster}
                </Alert>
            ) : rows.length === 0 ? (
                <Box sx={{ py: 4 }}>
                    <NoData
                        title={hasActiveFilters ? TEST_MODE_COPY.empty.noMatchTitle : TEST_MODE_COPY.empty.noneTitle}
                        description={hasActiveFilters ? TEST_MODE_COPY.empty.noMatchBody : TEST_MODE_COPY.empty.noneBody}
                        buttonText={hasActiveFilters ? TEST_MODE_COPY.empty.noMatchButton : null}
                        onClick={onClearFilters}
                    />
                </Box>
            ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 920 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={() => onToggleAllOnPage(pageIds, !allSelected)}
                                    />
                                </TableCell>
                                <TableCell sx={headerSx}>{COPY.headers.user}</TableCell>
                                <TableCell sx={headerSx}>{COPY.headers.groupTeam}</TableCell>
                                <TableCell sx={headerSx}>{COPY.headers.status}</TableCell>
                                <TableCell sx={headerSx}>{COPY.headers.window}</TableCell>
                                <TableCell sx={headerSx}>{COPY.headers.appTest}</TableCell>
                                <TableCell sx={headerSx}>{COPY.headers.device}</TableCell>
                                <TableCell sx={{ ...headerSx, textAlign: 'center' }}>{COPY.headers.flag}</TableCell>
                                <TableCell sx={{ ...headerSx, textAlign: 'right' }}>{COPY.headers.actions}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const inTest = Boolean(row.inTest);
                                const appTested = Boolean(row.testedApp);
                                const deviceTested = Boolean(row.testedDevice);
                                const flagText = row.exitedWithoutTest
                                    ? COPY.flags.exitedWithoutTest
                                    : row.pairedDeviceUntested
                                        ? COPY.flags.pairedDeviceUntested
                                        : null;

                                const entered = row.lastEnteredAt ? dayjs(row.lastEnteredAt) : null;
                                const expires = row.expiresAt ? dayjs(row.expiresAt) : null;
                                const meta1 = entered
                                    ? inTest
                                        ? COPY.window.entered(entered.fromNow())
                                        : COPY.window.lastSession(entered.fromNow())
                                    : COPY.window.never;
                                const meta2 = inTest && expires ? COPY.window.expires(expires.fromNow()) : null;
                                const expirySoon = expires ? expires.diff(dayjs(), 'hour') < 2 : false;

                                return (
                                    <TableRow key={row.userId} hover sx={{ '& td': { borderTop: border, borderBottom: 'none' } }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={Boolean(selected[row.userId])}
                                                onChange={() => onToggleRow(row.userId)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" gap="11px">
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '50%',
                                                        backgroundColor: GT_COLORS.primary,
                                                        color: 'common.white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {getInitials(row.name)}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: (theme.palette.primary as any).darker ?? theme.palette.primary.dark }}>
                                                        {row.name}
                                                    </Typography>
                                                    {row.employeeId ? (
                                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', mt: '2px' }}>
                                                            {row.employeeId}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: 13.5 }}>{row.group ?? <EmptyCell />}</Typography>
                                            <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: '2px' }}>
                                                {row.team ?? '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{statusChipFor(row, theme)}</TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: 13, color: 'grey.700' }}>{meta1}</Typography>
                                            {meta2 && (
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        mt: '2px',
                                                        color: expirySoon ? 'warning.dark' : 'text.disabled',
                                                        fontWeight: expirySoon ? 600 : 400,
                                                    }}
                                                >
                                                    {meta2}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {appTested ? (
                                                <CellBadge label={COPY.appTest.tested} tone="success" icon={<CheckIcon sx={{ fontSize: 13 }} />} />
                                            ) : (
                                                <CellBadge label={COPY.appTest.notTested} tone="muted" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {deviceTested ? (
                                                <CellBadge label={COPY.device.pairedTested} tone="success" icon={<CheckCircleRoundedIcon sx={{ fontSize: 13 }} />} />
                                            ) : Boolean(row.devicePaired) ? (
                                                <CellBadge label={COPY.device.pairedUntested} tone="warning" />
                                            ) : (
                                                <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>{COPY.device.none}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {flagText ? (
                                                <Tooltip title={flagText} arrow>
                                                    <WarningAmberRoundedIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            color: row.exitedWithoutTest ? 'error.main' : 'warning.main',
                                                            cursor: 'help',
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <EmptyCell />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.exitRequested ? (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        px: '10px',
                                                        py: '5px',
                                                        borderRadius: '8px',
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                        color: 'primary.dark',
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
                                                    {COPY.exitRequested}
                                                </Box>
                                            ) : inTest ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => onRequestExit(row)}
                                                    sx={{ borderRadius: '8px', fontSize: 12.5, fontWeight: 600 }}
                                                >
                                                    {COPY.requestExit}
                                                </Button>
                                            ) : (
                                                <EmptyCell />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePagination
                component="div"
                count={total}
                page={page - 1}
                rowsPerPage={perPage}
                rowsPerPageOptions={[25, 50, 100, 200]}
                onPageChange={(_e, newPage) => onPageChange(newPage + 1)}
                onRowsPerPageChange={(e) => onPerPageChange(parseInt(e.target.value, 10))}
                sx={{ borderTop: border }}
            />
        </Card>
    );
};
